import {
    JourneyNode,
    isSendWithItemStrategyActionStep,
} from '../types/journey-nodes'
import type { IActionStep } from '@pushly/models/lib/structs/journeys'
import { StepType } from '@pushly/models/lib/enums/journeys'
import {
    CatalogItemPickStrategy,
    CombinedItemStrategy,
} from '@pushly/models/lib/enums/catalogs'

export function computeCatalogItemPickStrategyOptions(
    baseOptions: CatalogItemPickStrategy[],
    priorCount: number,
): CatalogItemPickStrategy[] {
    const hasPrior = priorCount > 0
    return baseOptions.filter((opt) => {
        const requiresPrior =
            opt === CatalogItemPickStrategy.PREVIOUSLY_SELECTED || opt === CatalogItemPickStrategy.NEVER_SENT
        return requiresPrior ? hasPrior : true
    })
}

export function reconcileSelectedStrategy(
    nextOptions: CatalogItemPickStrategy[],
    current?: CatalogItemPickStrategy,
): CatalogItemPickStrategy | undefined {
    if (!nextOptions || nextOptions.length === 0) return undefined
    return current && nextOptions.includes(current) ? current : CatalogItemPickStrategy.MOST_RECENT
}

// Helper to reconcile the selected strategy for CombinedItemStrategy
export function reconcileSelectedCombinedStrategy(
    nextOptions: CombinedItemStrategy[],
    current?: CombinedItemStrategy,
): CombinedItemStrategy | undefined {
    if (!nextOptions || nextOptions.length === 0) return undefined
    // If current is valid, keep it; otherwise default to the first option
    return current && nextOptions.includes(current) ? current : nextOptions[0]
}

function isActionNode(n: JourneyNode): n is JourneyNode<IActionStep> {
    const type = n?.data?.step?.type
    // Support both enum and possible string runtime values
    return type === StepType.ACTION
}

export function getPriorSendActionCount(
    allNodes: Array<JourneyNode> | undefined,
    currentNodeId: string | undefined,
    currentActionIndex?: number,
): number {
    if (!allNodes || !allNodes.length) return 0

    // Extract ActionStep list in node order
    const actionsInOrder = allNodes.filter(isActionNode).map((n) => n.data.step)

    if (typeof currentActionIndex === 'number') {
        // Count send-item actions with a smaller actionIndex
        return actionsInOrder.filter((a) => {
            if (!isSendWithItemStrategyActionStep(a)) return false
            const idx = typeof a.meta?.actionIndex === 'number' ? a.meta.actionIndex : Number.MAX_SAFE_INTEGER
            return idx < currentActionIndex
        }).length
    }

    if (!currentNodeId) return 0

    const currentIdx = allNodes.findIndex((n) => n.id === currentNodeId)
    if (currentIdx <= 0) return 0

    const priorNodes = allNodes.slice(0, currentIdx)
    return priorNodes
        .filter(isActionNode)
        .map((n) => n.data.step)
        .filter(isSendWithItemStrategyActionStep).length
}
